import { logger } from "../../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Plus, Edit2, Trash2, Gift, Calendar } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPromo = {
  title: "",
  description: "",
  start_date: "",
  end_date: "",
  image_url: "",
  is_active: true,
};

export default function PromotionsManager() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState(emptyPromo);
  const { getAuthHeaders } = useAuth();

  const fetchPromotions = async () => {
    try {
      const response = await axios.get(`${API}/promotions/all`, getAuthHeaders());
      setPromotions(response.data);
    } catch (err) {
      logger.error("Error fetching promotions:", err);
      toast.error("Failed to load promotions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  const openCreateDialog = () => {
    setEditingPromo(null);
    setFormData(emptyPromo);
    setDialogOpen(true);
  };

  const openEditDialog = (promo) => {
    setEditingPromo(promo);
    setFormData({
      title: promo.title,
      description: promo.description,
      start_date: promo.start_date,
      end_date: promo.end_date,
      image_url: promo.image_url || "",
      is_active: promo.is_active,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.start_date || !formData.end_date) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      if (editingPromo) {
        await axios.put(`${API}/promotions/${editingPromo.id}`, formData, getAuthHeaders());
        toast.success("Promotion updated!");
      } else {
        await axios.post(`${API}/promotions`, formData, getAuthHeaders());
        toast.success("Promotion created!");
      }
      setDialogOpen(false);
      fetchPromotions();
    } catch (err) {
      toast.error("Failed to save promotion");
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm("Are you sure you want to delete this promotion?")) return;

    try {
      await axios.delete(`${API}/promotions/${promoId}`, getAuthHeaders());
      toast.success("Promotion deleted!");
      fetchPromotions();
    } catch (err) {
      toast.error("Failed to delete promotion");
    }
  };

  if (loading) {
    return <div className="text-gold animate-pulse">Loading promotions...</div>;
  }

  return (
    <div data-testid="promotions-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-white mb-2">Promotions Manager</h1>
          <p className="text-white/60">Create and manage special offers and promotions</p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-gold text-black hover:bg-gold-dark"
          data-testid="add-promo-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Promotion
        </Button>
      </div>

      {/* Promotions List */}
      {promotions.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Gift className="w-12 h-12 text-gold/30 mx-auto mb-4" />
          <p className="text-white/60">No promotions yet. Create your first promotion!</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {promotions.map((promo) => (
            <div
              key={promo.id}
              className={`glass-card p-6 ${!promo.is_active ? 'opacity-50' : ''}`}
              data-testid={`promo-card-${promo.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-heading text-xl text-white">{promo.title}</h3>
                    {!promo.is_active && (
                      <span className="px-2 py-1 bg-white/10 text-white/50 text-xs rounded">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-white/60 mb-3 line-clamp-2">{promo.description}</p>
                  <div className="flex items-center gap-2 text-white/50 text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{promo.start_date} — {promo.end_date}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(promo)}
                    className="border-white/20 text-white hover:border-gold hover:text-gold"
                    data-testid={`edit-promo-${promo.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(promo.id)}
                    className="border-white/20 text-white hover:border-red-500 hover:text-red-500"
                    data-testid={`delete-promo-${promo.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-casino-dark border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingPromo ? "Edit Promotion" : "Create Promotion"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="promo-form">
            <div>
              <Label className="text-white/80">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="e.g., Las Vegas Trip Giveaway"
                data-testid="promo-title-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1 min-h-[100px]"
                placeholder="Describe the promotion details..."
                data-testid="promo-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80">Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  data-testid="promo-start-input"
                />
              </div>
              <div>
                <Label className="text-white/80">End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  data-testid="promo-end-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80">Image URL (optional)</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="https://..."
                data-testid="promo-image-input"
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <Label className="text-white/80">Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="promo-active-switch"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gold text-black hover:bg-gold-dark"
                data-testid="promo-submit-btn"
              >
                {editingPromo ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
